'use client';

import React, { useRef, useState } from 'react';
import { ImagePlus, Link2, Loader2, Trash2, Upload } from 'lucide-react';
import { apiAsset } from '@/config';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_BYTES = 5 * 1024 * 1024;

interface MediaDropzoneProps {
  label: string;
  value?: string;
  uploadUrl: string;
  onChange: (url: string) => void;
  allowUrl?: boolean;
  hint?: string;
}

export function MediaDropzone({
  label,
  value = '',
  uploadUrl,
  onChange,
  allowUrl = true,
  hint = 'JPEG, PNG or WebP · max 5 MB',
}: MediaDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dimensions, setDimensions] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);

  const previewSrc = value ? apiAsset(value) : '';

  async function upload(file: File) {
    if (!uploadUrl) {
      setError('Image uploads need a platform upload URL.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Use a JPEG, PNG or WebP image.');
      setLastFile(null);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Images must be 5 MB or smaller.');
      setLastFile(null);
      return;
    }

    setLastFile(file);
    setUploading(true);
    setProgress(12);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('kind', 'image');
      setProgress(45);
      const res = await fetch(uploadUrl, { method: 'POST', body, headers: { Accept: 'application/json' } });
      setProgress(85);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || typeof data.url !== 'string') {
        setError(typeof data.error === 'string' ? data.error : 'Upload failed. You can retry.');
        return;
      }
      onChange(data.url);
      setProgress(100);
      setLastFile(null);
    } catch {
      setError('Upload failed. Check your connection and try again.');
    } finally {
      setUploading(false);
      window.setTimeout(() => setProgress(0), 400);
    }
  }

  return (
    <div className="block text-xs font-medium text-ink">
      {label}
      {value ? (
        <div className="relative mt-1 overflow-hidden rounded-xl border border-line bg-shell">
          <img
            src={previewSrc}
            alt=""
            className="h-28 w-full object-cover"
            onLoad={(event) => {
              const img = event.currentTarget;
              if (img.naturalWidth && img.naturalHeight) {
                setDimensions(`${img.naturalWidth}×${img.naturalHeight}`);
              }
            }}
            onError={() => setDimensions('Broken preview')}
          />
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-shell/90 px-2 py-1.5 text-[10px] text-ink">
            <span>{dimensions ?? 'Ready'}</span>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setDimensions(null);
              }}
              className="inline-flex items-center gap-1 rounded-md bg-danger/20 px-1.5 py-0.5 font-semibold text-danger hover:bg-danger/30"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </div>
        </div>
      ) : null}

      <div
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files?.[0];
          if (file) void upload(file);
        }}
        className={`mt-1 rounded-xl border border-dashed px-3 py-4 text-center transition ${
          dragging ? 'border-brand bg-brand/10' : 'border-line bg-shell/60 hover:border-muted'
        }`}
      >
        <ImagePlus className="mx-auto h-5 w-5 text-muted" />
        <p className="mt-2 text-[11px] font-normal text-muted">{hint}</p>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[11px] font-semibold text-ink hover:bg-shell disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
        </button>
        {progress > 0 && (
          <div className="mx-auto mt-2 h-1 w-full max-w-[12rem] overflow-hidden rounded-full bg-line">
            <div className="h-full bg-brand transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void upload(file);
          event.target.value = '';
        }}
      />

      {allowUrl && (
        <label className="mt-2 block">
          <span className="flex items-center gap-1 text-[11px] font-normal text-muted">
            <Link2 className="h-3 w-3" /> Or paste an image URL
          </span>
          <input
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="https://… or /storage/…"
            className="mt-1 w-full rounded-lg border border-line bg-shell px-2.5 py-2 text-xs text-ink outline-none focus:border-brand"
          />
        </label>
      )}

      {error && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <p className="text-[11px] font-semibold text-danger">{error}</p>
          {lastFile && !uploading && (
            <button
              type="button"
              onClick={() => void upload(lastFile)}
              className="text-[11px] font-semibold text-brand hover:underline"
            >
              Retry upload
            </button>
          )}
        </div>
      )}
    </div>
  );
}
